import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/solid';
import { HtmlFinalStep } from '@superfunnel/types';
import { useEffect, useRef, useState } from 'react';
import { useThumbnailSnapshots } from './ThumbnailHarvesterContext';

// Default dimensions (can be overridden via props)
const DEFAULT_HEIGHT = 140; // Desktop-like 16:10 aspect ratio
const VIEWPORT_WIDTH = 1440; // Typical laptop/desktop viewport width

interface StepThumbnailProps {
  /** The step ID to render */
  stepId: string;
  /** The type of step: 'step' or 'finalStep' */
  stepType?: 'step' | 'finalStep';
  /** Height of the thumbnail container (width is 100%) */
  height?: number;
  /** Additional className for the container */
  className?: string;
}

/**
 * Renders a lightweight, scaled-down srcdoc iframe preview of a step.
 *
 * Instead of each thumbnail loading its own full URL (which crashes the browser
 * with 20+ iframes), this uses pre-captured static HTML from a single hidden
 * "harvester" iframe (via ThumbnailHarvesterContext).
 *
 * Benefits:
 * - 0 network requests per thumbnail (HTML is a string in memory)
 * - 0 script execution (scripts are stripped during capture)
 * - ~300 MB total vs ~1.8 GB for separate src iframes
 * - Full style isolation preserved
 */
export const StepThumbnail = ({
  stepId,
  stepType = 'step',
  height = DEFAULT_HEIGHT,
  className = '',
}: StepThumbnailProps) => {
  const { getSnapshot, harvesting } = useThumbnailSnapshots();

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  // Track which snapshot has been written so we don't re-write the same content
  const writtenSnapshotRef = useRef<string | null>(null);

  // Only render iframe when in viewport (with margin)
  useEffect(() => {
    if (!containerRef.current) return;

    // Find the closest scrollable ancestor to use as root
    const findScrollParent = (el: HTMLElement): HTMLElement | null => {
      let parent = el.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflow + style.overflowY)) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollParent = findScrollParent(containerRef.current);

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries[0].isIntersecting);
      },
      { root: scrollParent, rootMargin: '2000px', threshold: 0 },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Measure container width for scaling
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const snapshot = getSnapshot(stepId, stepType);

  // Inject HTML into the iframe via document.write when snapshot changes
  useEffect(() => {
    if (!isVisible || !iframeRef.current || !snapshot) return;
    // Skip if we already wrote this exact snapshot
    if (writtenSnapshotRef.current === snapshot) return;

    const doc = iframeRef.current.contentDocument;
    if (!doc) return;

    doc.open();
    doc.write(snapshot);
    doc.close();
    writtenSnapshotRef.current = snapshot;
  }, [snapshot, isVisible]);

  // Reset written ref when scrolled out so content re-injects on return
  useEffect(() => {
    if (!isVisible) {
      writtenSnapshotRef.current = null;
    }
  }, [isVisible]);

  // Calculate scale based on measured container width
  const scale = containerWidth > 0 ? containerWidth / VIEWPORT_WIDTH : 0.56;
  const iframeHeight = containerWidth > 0 ? height / scale : height / 0.56;

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-md bg-gray-50 ${className}`}
      style={{ height }}
    >
      {isVisible && snapshot ? (
        <iframe
          ref={iframeRef}
          title="Step preview"
          sandbox="allow-same-origin"
          className="pointer-events-none absolute top-0 left-0 origin-top-left border-0 bg-white"
          style={{
            width: VIEWPORT_WIDTH,
            height: iframeHeight,
            transform: `scale(${scale})`,
          }}
          tabIndex={-1}
        />
      ) : harvesting ? (
        <div className="bg-base-200 h-full w-full animate-pulse" />
      ) : null}
    </div>
  );
};

interface FinalStepThumbnailProps {
  /** The final step to render */
  step: HtmlFinalStep;
  /** Height of the thumbnail container */
  height?: number;
  /** Additional className for the container */
  className?: string;
}

/**
 * Renders a thumbnail for a final step.
 * For HTML final steps, renders a scaled iframe preview.
 * For redirect final steps, shows a placeholder with the URL.
 */
export const FinalStepThumbnail = ({
  step,
  height = DEFAULT_HEIGHT,
  className = '',
}: FinalStepThumbnailProps) => {
  // Redirect final steps show a placeholder with the URL
  if (step.stepType === 'redirect') {
    return (
      <RedirectThumbnail url={step.url} height={height} className={className} />
    );
  }

  // HTML final steps render an iframe preview
  return (
    <StepThumbnail
      stepId={step.id}
      stepType="finalStep"
      height={height}
      className={className}
    />
  );
};

interface RedirectThumbnailProps {
  /** The redirect URL */
  url?: string;
  /** Height of the thumbnail container */
  height?: number;
  /** Additional className for the container */
  className?: string;
}

/**
 * Placeholder thumbnail for redirect steps showing the destination URL.
 */
export const RedirectThumbnail = ({
  url,
  height = DEFAULT_HEIGHT,
  className = '',
}: RedirectThumbnailProps) => (
  <div
    className={`flex flex-col items-center justify-center gap-1.5 rounded-md bg-gray-50 px-2 ${className}`}
    style={{ height }}
  >
    <ArrowRightStartOnRectangleIcon className="text-base-400 size-6" />
    <p className="text-base-500 text-center text-[10px] leading-tight">
      Redirects to
    </p>
    <p className="text-base-600 line-clamp-2 max-w-full text-center text-[10px] leading-tight font-medium break-all">
      {url || 'No URL set'}
    </p>
  </div>
);

interface ThumbnailPlaceholderProps {
  /** Height of the placeholder container */
  height?: number;
  /** Additional className for the container */
  className?: string;
}

/**
 * Fallback placeholder when no preview is available.
 */
export const ThumbnailPlaceholder = ({
  height = DEFAULT_HEIGHT,
  className = '',
}: ThumbnailPlaceholderProps) => (
  <div
    className={`flex items-center justify-center rounded-md bg-gray-50 ${className}`}
    style={{ height }}
  >
    <p className="text-base-400 text-xs">No preview</p>
  </div>
);
